import { useState, type KeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import { NavButtons } from "@/components/NavButtons";
import { FieldError } from "@/components/FieldError";
import { getHorizonScanningApi } from "./api";
import { setHorizonScanningSession } from "./sessionKeys";
import { useHorizonScanningWizard } from "./useHorizonScanningWizard";
import { WizardShell } from "./WizardShell";
import { TopicStep } from "./steps/TopicStep";
import { SourcesStep } from "./steps/SourcesStep";
import { FrequencyStep } from "./steps/FrequencyStep";
import { PersonaStep } from "./steps/PersonaStep";
import { EmailStep } from "./steps/EmailStep";

const art: Record<string, { src: string; alt: string } | undefined> = {
  topics: { src: "/img/placeholder-topic.svg", alt: "Topic tiles and keyword chips" },
  sources: { src: "/img/wizard-sources.svg", alt: "Selected regulator marks flowing into a weekly digest" },
  frequency: { src: "/img/placeholder-frequency.svg", alt: "Calendar showing weekly digest cadence" },
  persona: { src: "/img/placeholder-persona.svg", alt: "Role cards for practitioner, advisor, and vendor" },
  email: { src: "/img/placeholder-email.svg", alt: "Work email envelope as mailbox credential" },
};

const titles: Record<string, { title: string; description: string }> = {
  topics: {
    title: "What should we watch?",
    description: "Pick an industry pack, then tick the topics that match you. Fold out a tile to see the actual terms.",
  },
  sources: {
    title: "Which sources do you answer to?",
    description: "Suggested from your pack. Search the full catalogue. Logos are the credibility on this screen.",
  },
  frequency: {
    title: "How often should we send the digest?",
    description:
      "Weekly is the default and the only cadence during your free week. Daily and bi-weekly unlock when you continue.",
  },
  persona: {
    title: "I am",
    description: "This helps us route advisors and vendors to the right conversation later.",
  },
  email: {
    title: "Where should we send the digest?",
    description: "Work address only. Clicking the confirm link makes this mailbox the admin. No password.",
  },
};

export function HorizonScanningWizard() {
  const wizard = useHorizonScanningWizard();
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const copy = titles[wizard.stepId];
  const image = art[wizard.stepId];

  const submit = async () => {
    if (!wizard.canAdvance) {
      wizard.next();
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await getHorizonScanningApi().signup(wizard.state);
      setHorizonScanningSession("email", wizard.state.email.trim());
      setHorizonScanningSession("pending-id", res.pendingId);
      navigate("/horizonscanning/check-email", { replace: true });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Could not send the confirm email.");
    } finally {
      setSubmitting(false);
    }
  };

  const onNext = () => {
    if (wizard.isLast) {
      void submit();
      return;
    }
    wizard.next();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Enter" || e.shiftKey) return;
    const target = e.target as HTMLElement;
    if (target.tagName === "TEXTAREA" || target.tagName === "SELECT") return;
    e.preventDefault();
    onNext();
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div className="flex min-h-full flex-col" onKeyDown={onKeyDown}>
      <WizardShell
        stepIndex={wizard.stepIndex}
        totalSteps={wizard.totalSteps}
        progress={wizard.progress}
        imageSrc={image?.src}
        imageAlt={image?.alt}
      >
        <p className="mb-3 text-sm font-semibold uppercase tracking-wide text-brand-600">
          Step {wizard.stepIndex + 1}
        </p>
        <h1 className="text-2xl font-bold leading-tight text-ink sm:text-3xl">{copy.title}</h1>
        <p className="mt-3 text-lg text-ink-soft">{copy.description}</p>
        <div className="mt-7">
          {wizard.stepId === "topics" ? (
            <TopicStep pack={wizard.pack} state={wizard.state} onChange={wizard.update} />
          ) : null}
          {wizard.stepId === "sources" ? (
            <SourcesStep state={wizard.state} onChange={wizard.update} />
          ) : null}
          {wizard.stepId === "frequency" ? (
            <FrequencyStep state={wizard.state} onChange={wizard.update} />
          ) : null}
          {wizard.stepId === "persona" ? (
            <PersonaStep state={wizard.state} onChange={wizard.update} />
          ) : null}
          {wizard.stepId === "email" ? (
            <EmailStep state={wizard.state} error={wizard.error} onChange={wizard.update} />
          ) : null}
        </div>
        <FieldError message={wizard.error} />
        {submitError ? (
          <p role="alert" className="mt-2 text-sm font-medium text-red-600">
            {submitError}
          </p>
        ) : null}
        <NavButtons
          onBack={wizard.back}
          onNext={onNext}
          canGoBack={wizard.stepIndex > 0}
          canAdvance={!submitting}
          nextLabel={wizard.isLast ? (submitting ? "Sending…" : "Email me a confirm link") : "Continue"}
        />
      </WizardShell>
    </div>
  );
}
